import { useEffect, useRef, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Modal,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { motion, AnimatePresence } from "framer-motion";
import api from "../helpers/api";
import FeatherIcon from "feather-icons-react";
import { formatEnumName } from "../constants/utils";
import PermissionGuard from '../components/Common/PermissionGuard';
import { useTranslation } from 'react-i18next';

const Issues = () => {
  const { t } = useTranslation();
  document.title = `${t("shared.dynamic_values", "Dynamic Values")} | ArcMedix`;
  const [enumData, setEnumData] = useState([]);
  const [selectedEnum, setSelectedEnum] = useState(null);
  const [values, setValues] = useState([]);
  const [modal_large, setmodal_large] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchEnums = async () => {
    try {
      const { data } = await api.get("dynamic-enum/get-all");
      setEnumData(data.response || []);
      if (data.response && data.response.length > 0) {
        setSelectedEnum(data.response[0]);
        setValues(data.response[0].values || []);
      }
      return data.response;
    } catch (error) {
      console.error("Error fetching enums:", error);
      return null;
    }
  };

  const fetchValues = useCallback(async () => {
    if (!selectedEnum) return;

    setLoading(true);
    try {
      // In this case, we already have the values in the enumData
      // So we can just filter them based on search term
      let filteredValues = selectedEnum.values || [];

      if (searchTerm) {
        filteredValues = filteredValues.filter((value) =>
          value.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Check if we've reached the end of available values
      const hasMoreItems = filteredValues.length > count;
      setHasMore(hasMoreItems);
      
      // Slice the array to only show the current count
      setValues(filteredValues.slice(0, count));
    } catch (error) {
      console.error("Error fetching values:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [selectedEnum, count, searchTerm]);

  const loadMore = () => {
    setCount(prevCount => prevCount + 10);
  };

  const reloadValues = useCallback(() => {
    setCount(10);
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleSearch = useCallback((e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setCount(10);
    setHasMore(true);
  }, []);

  const handleEnumChange = (e) => {
    const enumId = parseInt(e.target.value);
    const selected = enumData.find((enumItem) => enumItem.id === enumId);
    setSelectedEnum(selected);
    setValues(selected?.values?.slice(0, count) || []);
    setCount(10);
    setHasMore(true);
  };

  const tog_large = (entry = null) => {
    setmodal_large(!modal_large);
    setSelectedEntry(
      entry || {
        value: "",
      }
    );
    setErrors({});
    setApiError("");
  };

  const handleModalClose = () => {
    setmodal_large(false);
    setSelectedEntry(null);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!selectedEntry?.value?.trim()) {
      newErrors.value = t('business.issues.value_required', 'Value is required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setApiError("");

      if (selectedEnum) {
        // Check if the new value already exists (excluding the original value we're editing)
        if (
          selectedEnum.values.some(
            (v) =>
              v === selectedEntry.value &&
              (!selectedEntry.originalValue ||
                v !== selectedEntry.originalValue)
          )
        ) {
          setApiError(t('business.issues.value_exists', 'This value already exists'));
          return;
        }

        const updatedValues = selectedEntry.originalValue
          ? selectedEnum.values.map((v) =>
              v === selectedEntry.originalValue ? selectedEntry.value : v
            )
          : [...selectedEnum.values, selectedEntry.value];

        await api.put(`dynamic-enum/update`, {
          id: selectedEnum.id,
          values: updatedValues,
        });

        // Refresh the enums data after update
        const updatedEnums = await fetchEnums();
        if (updatedEnums) {
          // Find and set the currently selected enum with updated values
          const currentEnum = updatedEnums.find(
            (e) => e.id === selectedEnum.id
          );
          if (currentEnum) {
            setSelectedEnum(currentEnum);
            setValues(currentEnum.values.slice(0, count) || []);
          }
        }

        handleModalClose();
      }
    } catch (error) {
      setApiError(error?.response?.data?.error || t('shared.something_went_wrong', 'Something went wrong!'));
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedEnum && selectedEntry) {
        const updatedValues = selectedEnum.values.filter(
          (v) => v !== selectedEntry.value
        );

        await api.put(`dynamic-enum/update`, {
          id: selectedEnum.id,
          values: updatedValues,
        });

        // Refresh the enums data after delete
        const updatedEnums = await fetchEnums();
        if (updatedEnums) {
          // Find and set the currently selected enum with updated values
          const currentEnum = updatedEnums.find(
            (e) => e.id === selectedEnum.id
          );
          if (currentEnum) {
            setSelectedEnum(currentEnum);
            setValues(currentEnum.values.slice(0, count) || []);
          }
        }

        setDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting value:", error);
      setApiError(t('business.issues.delete_failed', 'Failed to delete value'));
    }
  };

  useEffect(() => {
    fetchEnums();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchValues();
    }, 100);

    return () => clearTimeout(timer);
  }, [fetchValues, refreshTrigger]);

  return (
    <div className="page-content mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {selectedEnum && (
            <PermissionGuard permission="enum:create">
              <button
                type="button"
                className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
                onClick={() => tog_large()}
              >
                <FeatherIcon icon="plus" />
                {t('business.issues.add_new_enum_value', 'Add New {{enumName}} Value', { enumName: formatEnumName(selectedEnum?.name) })}
              </button>
            </PermissionGuard>
          )}
        </motion.div>
      </div>

      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="card-title text-success mb-0">
              {formatEnumName(selectedEnum?.name)}
            </h3>
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-md w-auto"
                value={selectedEnum?.id || ""}
                onChange={handleEnumChange}
              >
                <option value="" disabled>
                  {t('shared.select_value', 'Select Value')}
                </option>
                {enumData?.map((enumItem) => (
                  <option key={enumItem.id} value={enumItem.id}>
                    {formatEnumName(enumItem?.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>

        <CardBody>
          <div
            style={{
              overflowX: "auto",
              maxHeight: "calc(100vh - 300px)",
            }}
          >
            <table className="table table-hover mb-0">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>#</th>
                  <th>{t('shared.value', 'Value')}</th>
                  {selectedEnum && (
                    <>
                      <th>{t('shared.edit', 'Edit')}</th>
                      <th>{t('shared.delete', 'Delete')}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {values.map((value, ind) => (
                    <motion.tr
                      key={ind}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>{value.split("_").join(" ")}</td>
                      {selectedEnum && (
                        <>
                          <td>
                            <PermissionGuard permission="enum:update">
                              <span
                                className="text-warning"
                                role="button"
                                onClick={() =>
                                  tog_large({
                                    value,
                                    originalValue: value,
                                  })
                                }
                              >
                                <FeatherIcon icon="edit" />
                              </span>
                            </PermissionGuard>
                          </td>
                          <td>
                            <PermissionGuard permission="enum:delete">
                              <span
                                className="text-danger"
                                role="button"
                                onClick={() => {
                                  setSelectedEntry({ value });
                                  setDeleteModal(true);
                                }}
                              >
                                <FeatherIcon icon="trash-2" />
                              </span>
                            </PermissionGuard>
                          </td>
                        </>
                      )}
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-center mt-3">
            {loading ? (
              <Spinner color="primary" />
            ) : hasMore ? (
              <button 
                className="btn btn-primary"
                onClick={loadMore}
              >
                {t('shared.load', 'Load More')}
              </button>
            ) : (
              values.length > 0 && (
                <div className="text-muted">{t('business.issues.no_more_values', 'No more values to load')}</div>
              )
            )}
          </div>
        </CardBody>
      </Card>

      {/* Add/Edit Modal */}
      <Modal size="lg" isOpen={modal_large} toggle={() => tog_large()}>
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              {selectedEntry?.originalValue ? t('business.issues.edit_value', 'Edit Value') : t('business.issues.add_value', 'Add Value')}
            </h5>
            <button
              onClick={handleModalClose}
              type="button"
              className="btn-close"
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>

          <div className="modal-body">
            <form onSubmit={handleUpdate}>
              <Row className="mb-4">
                <label htmlFor="enumValue" className="col-sm-3 col-form-label">
                  {t('shared.value', 'Value')}
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    id="enumValue"
                    className={`form-control ${
                      errors.value ? "is-invalid" : ""
                    }`}
                    value={selectedEntry?.value || ""}
                    onChange={(e) =>
                      setSelectedEntry({
                        ...selectedEntry,
                        value: e.target.value,
                      })
                    }
                  />
                  {errors.value && (
                    <small className="text-danger">{errors.value}</small>
                  )}
                </Col>
              </Row>

              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                >
                  {selectedEntry?.originalValue ? t('shared.update', 'Update') : t('shared.add', 'Add')}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">{t('shared.confirm_delete', 'Confirm Delete')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          <p>
            {t('business.issues.confirm_delete_value', 'Are you sure you want to delete this value? This action cannot be undone.')}
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(false)}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
          >
            {t('shared.delete', 'Delete')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Issues;